import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import {
    useIsProtectedRoute,
    useLocationPotentialRouteState,
    useIsLoginRoute,
    useIsWidgetRoute,
    useIsCircleRoute,
    useIsLoadFinderRoute,
    useIsTrucklistRoute,
} from "@/routing";
import { AuthLayout, MainLayout } from "@/layouts/index";
import useAuthStateListener from "@/auth/useAuthStateListener";
import { setSearchType } from "@/state/searchInputSlice";
import { useIsWebUser } from "@/auth/roles";
import { TranslatedText } from "@/components/common/text";
import { Footer } from "@/components/layout";
import { useTheme } from "@/components/layout/useTheme";
import { cn } from "@/utils/css";
import { Toaster } from "@/components/common/notification/Toaster";
import { usePageTracking } from "@/services/analytics";

export const InitLayout = memo(({ children }) => {
    //TODO Refactor to make single hook with all params
    const isProtectedRoute = useIsProtectedRoute();
    const isLoginRoute = useIsLoginRoute();
    const isWidgetRoute = useIsWidgetRoute();
    const isCircleRoute = useIsCircleRoute();
    const isLoadFinderRoute = useIsLoadFinderRoute();
    const isTrucklistRoute = useIsTrucklistRoute();
    const routeSearchType = useLocationPotentialRouteState();
    const dispatch = useDispatch();
    const { data: userData } = useSelector((state) => state.profile);
    const offersIsOpen = useSelector((state) => state.offers.isOpen);
    const searchOpen = useSelector((state) => state.searchInput.searchOpen);
    const searchTopRef = useRef(null);
    const location = useLocation();
    const isWebUser = useIsWebUser();
    const [initialLoad, setInitialLoad] = useState(true);

    useAuthStateListener();
    usePageTracking();
    const { isDarkTheme } = useTheme();

    useLayoutEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth",
        });
    }, [location.pathname]);

    useEffect(() => {
        // Don't set searchType to "home" - homepage should use "loadboard" or "load_rates"
        // based on the user's locked status, which is handled in searchInputSlice and Homepage
        if (routeSearchType && routeSearchType !== "home") {
            dispatch(setSearchType(routeSearchType));
        }
    }, [routeSearchType]);

    useEffect(() => {
        if (searchOpen && searchTopRef.current) {
            searchTopRef.current.scrollIntoView({
                behavior: "smooth",
            });
        }
    }, [searchOpen]);

    useEffect(() => {
        if (!!userData && initialLoad) {
            setInitialLoad(false);
        }
    }, [userData]);

    return (
        <div
            className={cn("w-full min-h-screen h-full flex flex-col items-center font-sans justify-start", {
                "transition-colors duration-500 ease-in-out": !initialLoad,
                "overflow-x-hidden": isLoadFinderRoute,
            })}
        >
            {isCircleRoute ? (
                <div className="absolute top-0 left-0 w-full min-h-screen -z-10 overflow-hidden">
                    <div
                        className={cn(
                            "absolute top-0 left-0 w-full h-[100vw] bg-[radial-gradient(50%_50%_at_50%_50%,_var(--tw-gradient-from)_0%,_var(--tw-gradient-to)_100%)] from-cm-p-accent-500 to-cm-container",
                            {
                                "opacity-[0.07]": isDarkTheme,
                                "opacity-[0.1]": !isDarkTheme,
                            },
                        )}
                    />
                </div>
            ) : null}

            {isProtectedRoute ? (
                <AuthLayout>
                    {isWebUser === undefined || !userData ? null : (
                        <MainLayout searchTopRef={searchTopRef} hideSearch={!isWebUser} widget={isWidgetRoute}>
                            {isWebUser ? (
                                children
                            ) : (
                                <div className="flex items-center justify-center w-full h-64">
                                    <TranslatedText className="text-cm-p-text-primary text-lg">
                                        You don't have access to this page
                                    </TranslatedText>
                                </div>
                            )}
                        </MainLayout>
                    )}
                </AuthLayout>
            ) : isLoginRoute || isTrucklistRoute ? (
                <>{children}</>
            ) : (
                <MainLayout searchTopRef={searchTopRef}>{children}</MainLayout>
            )}
            {!isLoadFinderRoute && !isTrucklistRoute && <Footer showPrivacy={!isWidgetRoute} />}
            <Toaster position="bottom-right" duration={10000} hidden={offersIsOpen} />
            <Toaster id="top-right" position="top-right" duration={3000} offset={{ top: 64, right: 16 }} />
        </div>
    );
});
